/**
 * @fileoverview Painel do modo "Lista salva" (Bases) no node bulk_message.
 * Lista as Bases de Contatos da empresa, permite selecionar várias (checkbox),
 * buscar por nome e ORDENAR as selecionadas (a ordem é a ordem de disparo).
 * Bases em processamento/erro aparecem desabilitadas com o status.
 *
 * Props: { baseIds: number[], onChange: (orderedIds:number[]) => void }
 */

import React, { useMemo, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import {
  Database, Search, RefreshCw, Check, ChevronUp, ChevronDown, X,
  Users, Loader, AlertCircle,
} from "lucide-react";
import useContactBases from "../../hooks/useContactBases";

// Vocabulário real de ContactBase.status (toda base nasce awaiting_mapping).
const STATUS = {
  awaiting_mapping: { label: "Aguardando mapeamento", color: "#f59e0b", bg: "rgba(245,158,11,0.15)" },
  processing: { label: "Processando", color: "#60a5fa", bg: "rgba(96,165,250,0.15)", spin: true },
  ready: { label: "Pronta", color: "#34d399", bg: "rgba(52,211,153,0.15)" },
  ready_with_errors: { label: "Com erros", color: "#fb923c", bg: "rgba(251,146,60,0.15)" },
  failed: { label: "Falhou", color: "#f87171", bg: "rgba(248,113,113,0.15)" },
  archived: { label: "Arquivada", color: "#9ca3af", bg: "rgba(156,163,175,0.12)" },
};

const SELECTABLE = ["ready", "ready_with_errors"];

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.awaiting_mapping;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 7px",
        borderRadius: 999, fontSize: 9, fontWeight: 600, color: s.color, background: s.bg,
        whiteSpace: "nowrap",
      }}
    >
      {s.spin && <Loader size={9} style={{ animation: "spin 1s linear infinite" }} />}
      {s.label}
    </span>
  );
};

const BulkBasesPanel = ({ baseIds, onChange }) => {
  const isDark = useTheme().palette.type === "dark";
  const W = (a) => (isDark ? `rgba(255,255,255,${a})` : `rgba(20,22,30,${a})`);

  const labelStyle = {
    fontSize: 11,
    fontWeight: 600,
    color: W(0.7),
    marginBottom: 4,
    display: "block",
  };

  const inputStyle = {
    width: "100%",
    padding: "7px 10px 7px 30px",
    borderRadius: 6,
    border: "1px solid " + W(0.08),
    background: W(0.03),
    color: W(0.9),
    fontSize: 12,
    outline: "none",
    boxSizing: "border-box",
  };

  const { bases, loading, refresh } = useContactBases();
  const [search, setSearch] = useState("");

  const selectedIds = Array.isArray(baseIds) ? baseIds : [];

  // Mapa id -> base para resolver as selecionadas (mesmo arquivadas/ausentes).
  const byId = useMemo(() => {
    const m = {};
    (bases || []).forEach((b) => { m[b.id] = b; });
    return m;
  }, [bases]);

  // Lista disponível: oculta arquivadas; aplica busca por nome.
  const available = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (bases || [])
      .filter((b) => b.status !== "archived")
      .filter((b) => (term ? String(b.name || "").toLowerCase().indexOf(term) !== -1 : true));
  }, [bases, search]);

  const totalSelected = useMemo(
    () => selectedIds.reduce((sum, id) => sum + ((byId[id] && byId[id].validRows) || 0), 0),
    [selectedIds, byId]
  );

  const toggle = (b) => {
    if (SELECTABLE.indexOf(b.status) === -1) return;
    const idx = selectedIds.indexOf(b.id);
    if (idx === -1) onChange(selectedIds.concat(b.id));
    else onChange(selectedIds.filter((x) => x !== b.id));
  };

  const move = (id, dir) => {
    const idx = selectedIds.indexOf(id);
    const to = idx + dir;
    if (idx === -1 || to < 0 || to >= selectedIds.length) return;
    const next = selectedIds.slice();
    const tmp = next[idx];
    next[idx] = next[to];
    next[to] = tmp;
    onChange(next);
  };

  const removeSel = (id) => onChange(selectedIds.filter((x) => x !== id));

  return (
    <div>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>

      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <label style={{ ...labelStyle, flex: 1, marginBottom: 0 }}>Bases de contatos</label>
        <div
          onClick={() => refresh()}
          title="Recarregar"
          style={{ cursor: "pointer", padding: 4, color: W(0.5), display: "inline-flex" }}
        >
          <RefreshCw size={12} />
        </div>
      </div>

      {/* Busca */}
      <div style={{ position: "relative", marginBottom: 8 }}>
        <Search size={13} style={{ position: "absolute", left: 9, top: 9, color: W(0.4) }} />
        <input
          style={inputStyle}
          placeholder="Buscar base pelo nome…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Lista de bases disponíveis */}
      {loading ? (
        <div style={{ padding: "12px 0", textAlign: "center" }}>
          <CircularProgress size={16} style={{ color: "#818cf8" }} />
        </div>
      ) : available.length === 0 ? (
        <div
          style={{
            padding: 12, borderRadius: 8, border: "1px dashed " + W(0.12),
            fontSize: 11, color: W(0.5), textAlign: "center",
          }}
        >
          <AlertCircle size={16} style={{ marginBottom: 6, opacity: 0.6 }} />
          <div>Nenhuma base encontrada. Envie planilhas em "Gerenciar Bases".</div>
        </div>
      ) : (
        <div
          style={{
            maxHeight: 200, overflowY: "auto",
            border: "1px solid " + W(0.06), borderRadius: 8,
          }}
        >
          {available.map((b) => {
            const selectable = SELECTABLE.indexOf(b.status) !== -1;
            const active = selectedIds.indexOf(b.id) !== -1;
            return (
              <div
                key={b.id}
                onClick={() => toggle(b)}
                title={selectable ? "" : "Base indisponível para disparo"}
                style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                  cursor: selectable ? "pointer" : "not-allowed",
                  opacity: selectable ? 1 : 0.5,
                  background: active ? "rgba(99,102,241,0.14)" : "transparent",
                  borderBottom: "1px solid " + W(0.04),
                  transition: "background 120ms ease",
                }}
              >
                <div
                  style={{
                    width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: active ? "none" : "1px solid " + W(0.2),
                    background: active ? "#4f7cff" : "transparent",
                  }}
                >
                  {active && <Check size={11} color="#fff" strokeWidth={3} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12, color: W(0.88), fontWeight: active ? 600 : 400,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}
                  >
                    {b.name}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, color: W(0.45) }}>
                      <Users size={10} /> {(b.validRows || 0).toLocaleString("pt-BR")}
                    </span>
                    {!selectable && <StatusBadge status={b.status} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Selecionadas — ordem de disparo */}
      {selectedIds.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <label style={{ ...labelStyle, marginBottom: 6 }}>
            Ordem de disparo ({selectedIds.length} base{selectedIds.length === 1 ? "" : "s"})
          </label>
          <div
            style={{
              border: "1px solid rgba(99,102,241,0.22)", borderRadius: 8,
              background: "rgba(99,102,241,0.06)", overflow: "hidden",
            }}
          >
            {selectedIds.map((id, i) => {
              const b = byId[id];
              const name = b ? b.name : `Base #${id}`;
              const count = b ? (b.validRows || 0) : 0;
              return (
                <div
                  key={id}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "7px 10px",
                    borderBottom: i < selectedIds.length - 1 ? "1px solid " + W(0.05) : "none",
                  }}
                >
                  <span
                    style={{
                      width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: "rgba(99,102,241,0.25)", color: "#c7d2fe", fontSize: 10, fontWeight: 700,
                    }}
                  >
                    {i + 1}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: W(0.85), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {name}
                    </div>
                    <div style={{ fontSize: 10, color: W(0.4) }}>
                      {count.toLocaleString("pt-BR")} contato{count === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div
                    onClick={() => move(id, -1)}
                    title="Subir"
                    style={{ padding: 3, cursor: i === 0 ? "not-allowed" : "pointer", color: i === 0 ? W(0.2) : W(0.6) }}
                  >
                    <ChevronUp size={14} />
                  </div>
                  <div
                    onClick={() => move(id, 1)}
                    title="Descer"
                    style={{ padding: 3, cursor: i === selectedIds.length - 1 ? "not-allowed" : "pointer", color: i === selectedIds.length - 1 ? W(0.2) : W(0.6) }}
                  >
                    <ChevronDown size={14} />
                  </div>
                  <div
                    onClick={() => removeSel(id)}
                    title="Remover da configuração"
                    style={{ padding: 3, cursor: "pointer", color: "rgba(239,68,68,0.6)" }}
                  >
                    <X size={14} />
                  </div>
                </div>
              );
            })}
          </div>
          <div
            style={{
              marginTop: 8, display: "flex", alignItems: "center", gap: 6,
              fontSize: 12, fontWeight: 600, color: "#a5b4fc",
            }}
          >
            <Database size={13} color="#818cf8" />
            {totalSelected.toLocaleString("pt-BR")} contato{totalSelected === 1 ? "" : "s"} válido{totalSelected === 1 ? "" : "s"} no total
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkBasesPanel;
